import { TabProps } from "@/components/Tab";
import {
  BrandWithCategoriesResponse,
  MakerWithCategoriesResponse,
} from "@/web-client/api";
import { useMemo } from "react";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";

export enum CompanyTabState {
  Interior = 1,
  BuildingMaterial,
  Post,
  Project,
  Catalog,
  List,
}

const useCompanyTabs = (
  isMaker: boolean,
  company: MakerWithCategoriesResponse | BrandWithCategoriesResponse,
  currentState: CompanyTabState,
  query: ProductSearchQueryParameter,
): TabProps[] => {
  // 建材検索から切り替える際にsortパラメーターが残ってしまうので、
  // sortパラメーターを削除したものを用意する
  const queryWithoutSort = useMemo(() => {
    const clone = { ...query };
    delete clone.sort;

    return clone;
  }, [query]);

  // 建材も家具も保持している
  const hasMulti = useMemo<boolean>(
    () =>
      company.building_material_product_variations_count > 0 &&
      company.interior_product_variations_count > 0,
    [company],
  );

  const tabs = useMemo<TabProps[]>(() => {
    const tabs = [];
    const base = isMaker ? "/makers" : "/brands";
    // インテリア
    company.interior_product_variations_count > 0 &&
      tabs.push({
        isCurrent: currentState === CompanyTabState.Interior,
        to: {
          pathname: `${base}/${company.id}/interiors`,
          query: hasMulti ? null : query, // 家具建材を保持している場合はクエリを引き継がない
        },
        label: "家具",
      });
    // 建材
    company.building_material_product_variations_count > 0 &&
      tabs.push({
        isCurrent: currentState === CompanyTabState.BuildingMaterial,
        to: {
          pathname: `${base}/${company.id}/building_materials`,
          query: hasMulti ? null : query, // 家具建材を保持している場合はクエリを引き継がない
        },
        label: "建材",
      });

    // 写真
    if (company.posts_count > 0) {
      tabs.push({
        isCurrent: currentState === CompanyTabState.Post,
        to: {
          pathname: `${base}/${company.id}/posts`,
          query: queryWithoutSort,
        },
        label: "写真",
      });
    }

    if (isMaker) {
      tabs.push({
        isCurrent: currentState === CompanyTabState.Project,
        to: {
          pathname: `${base}/${company.id}/projects`,
          query: queryWithoutSort,
        },
        label: "関連プロジェクト",
      });
    }

    if (isMaker) {
      tabs.push({
        isCurrent: currentState === CompanyTabState.List,
        to: {
          pathname: `${base}/${company.id}/lists`,
          query: queryWithoutSort,
        },
        label: "リスト",
      });
    }

    // カタログ
    // ブランドにカタログはない
    if (isMaker) {
      if ((company as MakerWithCategoriesResponse).catalogs_count > 0) {
        tabs.push({
          isCurrent: currentState === CompanyTabState.Catalog,
          to: {
            pathname: `${base}/${company.id}/catalogs`,
          },
          label: "カタログ",
        });
      }
    }

    return tabs;
  }, [company, isMaker, currentState, hasMulti, query, queryWithoutSort]);

  return tabs;
};

export default useCompanyTabs;
